import { calculateScoresFromCheckInAnswers } from 'components/checkIn/questionnaire/AssessmentScoreUtility';
import { produce } from 'immer';
import { toAnswerMap } from 'services/checkIn/questionnaire-response';

const initialState = {
  results: [],
  selectedDate: '',
  questionnaireResponse: {
    responses: null,
    error: null,
  },
};

// Some selectors.
export const selectResults = ({ results }) => results?.results || [];
export const selectResultsSelectedDate = ({ results }) => results?.selectedDate || [];
export const selectQuestionnaireResponse = ({ results }) =>
  results?.questionnaireResponse?.responses?.filter(
    (item) => item.resource.authored === results.selectedDate,
  ) || [];

export const ResultScoreKeys = {
  PAIN: 'pain',
  MOOD: 'mood',
  SLEEP: 'sleep',
  WORRY: 'worry',
  SUBSTANCE: 'substance',
  DATE: 'date',
};
export const createResultScores = (
  painScore,
  moodScore,
  worryScore,
  sleepScore,
  substanceScore,
  date,
) => {
  return {
    [ResultScoreKeys.PAIN]: painScore,
    [ResultScoreKeys.MOOD]: moodScore,
    [ResultScoreKeys.WORRY]: worryScore,
    [ResultScoreKeys.SLEEP]: sleepScore,
    [ResultScoreKeys.SUBSTANCE]: substanceScore,
    [ResultScoreKeys.DATE]: date,
  };
};

export const SET_RESULTS = 'SET_RESULTS';
export const setResults = (resultsArray) => ({
  type: SET_RESULTS,
  payload: resultsArray,
});

export const CLEAR_RESULTS = 'CLEAR_RESULTS';
export const clearResults = () => ({
  type: CLEAR_RESULTS,
});

export const SET_SELECTED_DATE = 'SET_SELECTED_DATE';
export const setSelectedDate = (selectedDate) => ({
  type: SET_SELECTED_DATE,
  payload: selectedDate,
});

export const SET_QUESTIONNAIRE_RESPONSES = 'SET_QUESTIONNAIRE_RESPONSES';
export const setQuestionnaireResponses = (questionnaireResponses) => ({
  type: SET_QUESTIONNAIRE_RESPONSES,
  payload: questionnaireResponses,
});

export const SET_QUESTIONNAIRE_RESPONSE_FAILURE = 'SET_QUESTIONNAIRE_RESPONSE_FAILURE';
export const setQuestionnaireResponseFailure = (error) => ({
  type: SET_QUESTIONNAIRE_RESPONSE_FAILURE,
  payload: {
    ...error,
  },
});

export const CLEAR_QUESTIONNAIRE_RESPONSE_FAILURE = 'CLEAR_QUESTIONNAIRE_RESPONSE_FAILURE';
export const clearQuestionnaireResponseFailure = () => ({
  type: CLEAR_QUESTIONNAIRE_RESPONSE_FAILURE,
});

const UPDATE_QUESTIONNAIRE_RESPONSE_REQUEST = 'UPDATE_QUESTIONNAIRE_RESPONSE_REQUEST';
export const setUpdateQuestionnaireResponseRequest = {
  type: UPDATE_QUESTIONNAIRE_RESPONSE_REQUEST,
};

const UPDATE_QUESTIONNAIRE_RESPONSE_SUCCESS = 'UPDATE_QUESTIONNAIRE_RESPONSE_SUCCESS';
export const setUpdateQuestionnaireResponseSuccess = (questionnaireResponse) => ({
  type: UPDATE_QUESTIONNAIRE_RESPONSE_SUCCESS,
  payload: questionnaireResponse,
});

const UPDATE_QUESTIONNAIRE_RESPONSE_FAILURE = 'UPDATE_QUESTIONNAIRE_RESPONSE_FAILURE';
export const setUpdateQuestionnaireResponseFailure = (error) => ({
  type: UPDATE_QUESTIONNAIRE_RESPONSE_FAILURE,
  payload: {
    ...error,
  },
});

export const calculateResultsArrayFromQuestionnaireResponses = (questionnaireResponseArray) => {
  let resultsArray = [];

  // For each questionnaire response, calculate the result which includes the calculations and the date.
  if (!!questionnaireResponseArray && questionnaireResponseArray.length > 0) {
    questionnaireResponseArray.forEach((questionnaireResponse) => {
      if (
        !!questionnaireResponse &&
        questionnaireResponse?.resource?.resourceType === 'QuestionnaireResponse'
      ) {
        const answerMap = toAnswerMap(questionnaireResponse.resource.item);
        if (Object.keys(answerMap).length > 0) {
          resultsArray.push({
            ...calculateScoresFromCheckInAnswers(answerMap),
            date: questionnaireResponse.resource.authored,
            notes: questionnaireResponse.resource.extension?.[0]?.valueCode || '',
          });
        }
      }
    });
  }
  return resultsArray;
};

const sortResultsByDate = (results) => {
  // Sort by date.
  results.sort(function (first, second) {
    return new Date(first.date) - new Date(second.date);
  });
};

const results = (state = initialState, action) =>
  produce(state, (draft) => {
    const { type, payload } = action;
    switch (type) {
      case SET_RESULTS:
        if (payload.length > 1) {
          sortResultsByDate(payload);
        }
        draft.results = payload;
        break;
      case CLEAR_RESULTS:
        draft.results = [];
        draft.selectedDate = '';
        draft.questionnaireResponse = {
          responses: null,
          error: null,
        };
        break;
      case SET_SELECTED_DATE:
        draft.selectedDate = payload;
        break;
      case SET_QUESTIONNAIRE_RESPONSES:
        // This is an array of QuestionnaireResponses from the bundle.
        draft.questionnaireResponse.responses = action.payload;
        // From this bundle, calculate the scores.
        draft.results = calculateResultsArrayFromQuestionnaireResponses(action.payload);
        if (draft?.results?.length > 1) {
          sortResultsByDate(draft.results);
        }
        draft.questionnaireResponse.error = null;
        break;
      case SET_QUESTIONNAIRE_RESPONSE_FAILURE:
        draft.questionnaireResponse.responses = null;
        draft.questionnaireResponse.error = action.payload;
        break;
      case CLEAR_QUESTIONNAIRE_RESPONSE_FAILURE:
        draft.questionnaireResponse.responses = null;
        draft.questionnaireResponse.error = null;
        break;
      case UPDATE_QUESTIONNAIRE_RESPONSE_REQUEST:
        draft.questionnaireResponse.error = null;
        break;
      case UPDATE_QUESTIONNAIRE_RESPONSE_SUCCESS:
        draft.questionnaireResponse.responses = draft.questionnaireResponse.responses?.map(
          (item) => {
            if (item.resource.id === action.payload.id) {
              return {
                resource: action.payload,
              };
            }
            return item;
          },
        );
        break;
      case UPDATE_QUESTIONNAIRE_RESPONSE_FAILURE:
        draft.questionnaireResponse.error = action.payload;
        break;
      default:
        break;
    }
  });

export default results;
